import { useSyncExternalStore } from 'react';

/**
 * An IndexedDB-backed outbox for site-report writes, spec 08 §8: "Photo upload queues in
 * IndexedDB and retries when connectivity returns. A site with no signal is normal, and
 * losing a report because of it is not acceptable." This is the same idea for the report
 * itself — a foreman who spent ten minutes typing must not lose it to a bad signal.
 *
 * Three rules a naive queue gets wrong, kept here rather than at each call site:
 *
 * 1. **A 4xx is not worth retrying.** Validation failures and conflicts fail identically
 *    forever; keeping one queued blocks every write behind it. It is dropped and moved to
 *    `rejected`, body and all, so the caller can show the person what they wrote and why it
 *    did not go through — never silently discarded. `rejected` is written to storage of its
 *    own before the outbox row goes, because "kept in memory" does not survive a reload, an
 *    Android tab eviction, or the full-page navigation a Google sign-in is.
 * 2. **A 5xx or a network failure IS worth retrying.** `flush()` stops at the first one,
 *    oldest-first, so nothing later jumps the queue and order is preserved for the retry.
 * 3. **A 401 is neither.** It is the most likely answer a *deferred* write gets — sessions
 *    are 12 hours and a queued report is filed hours before it sends — and it is the one
 *    refusal that is completely recoverable. Dropping it destroys a report because the person
 *    was not signed in at the moment the phone found signal. It stays queued, the snapshot
 *    says `authBlocked`, and the caller re-flushes once there is a session again.
 * 4. **Replay is made idempotent by the caller, not guessed at from a conflict.** A queued
 *    site report carries its own `idempotencyKey`, so a replay of a write whose response was
 *    lost is answered 2xx with the report it already made. A `site_report_exists` conflict
 *    therefore means what it says: a *different* report already holds that day. Reading it as
 *    success — which this queue used to do — silently threw away the second report and told
 *    the person it sent.
 *
 * Storage can throw (private browsing, a full disk, an old WebView): every IndexedDB call is
 * wrapped, and once storage proves unavailable this degrades to "cannot queue" rather than
 * white-screening — the caller falls back to plain online-only posting.
 */

const DB_NAME = 'broccoli-offline-queue';
const DB_VERSION = 2;
const STORE = 'outbox';
/** Writes the server refused, kept durably so a reload does not finish the job the 4xx started. */
const REJECTED_STORE = 'rejected';
/** Mirrors `shared/api/client.ts`'s `BASE` — duplicated rather than imported so this module
 *  has no dependency on that file's internals, only on the wire path it already builds. */
const API_BASE = '/api';

/** Statuses that mean "not signed in", not "this write is wrong". */
const AUTH_STATUSES = new Set([401, 419, 440]);

const INITIAL_BACKOFF_MS = 5_000;
const MAX_BACKOFF_MS = 60_000;

export type QueueMethod = 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export interface QueueRequestInput {
  method: QueueMethod;
  path: string;
  body?: unknown;
}

export interface QueuedWrite extends QueueRequestInput {
  id: string;
  createdAt: number;
}

/** A write the server will never accept, kept around (with the body the person typed) so it
 *  can be shown rather than lost. */
export interface RejectedWrite extends QueuedWrite {
  rejectedAt: number;
  errorCode: string;
  errorMessage: string;
}

export interface OfflineQueueSnapshot {
  pendingCount: number;
  /** The queued writes themselves, so a screen can show what is still only on this phone. */
  pending: QueuedWrite[];
  isFlushing: boolean;
  isOnline: boolean;
  /** The last attempt was refused for want of a session. Nothing was dropped; sign in again. */
  authBlocked: boolean;
  rejected: RejectedWrite[];
}

export interface FlushResult {
  sent: number;
  rejected: number;
  remaining: number;
}

export interface OfflineQueueOptions {
  /** Injected for tests (and only for tests) instead of adding a fake-indexeddb dependency. */
  indexedDB?: IDBFactory;
  fetchImpl?: typeof fetch;
}

export interface OfflineQueueHandle {
  /** `replaceId` swaps a queued write for a corrected version of itself, in place. */
  enqueue: (input: QueueRequestInput, opts?: { replaceId?: string }) => Promise<QueuedWrite>;
  /** Removes a queued write without sending it — used when the same write went out live. */
  dequeue: (id: string) => Promise<void>;
  flush: () => Promise<FlushResult>;
  getPending: () => QueuedWrite[];
  getRejected: () => RejectedWrite[];
  dismissRejected: (id: string) => void;
  subscribe: (listener: () => void) => () => void;
  getSnapshot: () => OfflineQueueSnapshot;
  /** Removes the online/offline listeners and clears any pending backoff timer. Tests must
   *  call this so a scheduled retry does not fire after the test (and its mocks) are gone. */
  dispose: () => void;
}

export class OfflineQueueUnavailableError extends Error {
  constructor(cause?: string) {
    super(
      cause ? `offline queue storage unavailable: ${cause}` : 'offline queue storage unavailable',
    );
    this.name = 'OfflineQueueUnavailableError';
  }
}

/** True for a failure worth queuing (dropped connection, the server's own fault, or an expired
 *  session) — false for a refusal that will happen again no matter how many times it is sent.
 *  Exported so a submit handler can decide with the same rule the queue itself replays with. */
export function isQueueableFailure(error: unknown): boolean {
  const status = (error as { status?: number } | null)?.status;
  // No `status` means the request never got an HTTP response at all — `fetch` itself threw,
  // which on the open web means "no connectivity", not "the server said no".
  if (status === undefined) return true;
  // A 401 on a live submit is the same problem as a 401 on a replay: the person is signed out,
  // not wrong. Queuing it keeps the report when the shell redirects the tab to /login, which
  // is what happens next and would otherwise take the form and its contents with it.
  if (AUTH_STATUSES.has(status)) return true;
  return status >= 500;
}

/* ── IndexedDB plumbing — every call wrapped, nothing here assumes it will succeed ────────── */

function safeGlobalIndexedDB(): IDBFactory | undefined {
  try {
    return typeof indexedDB === 'undefined' ? undefined : indexedDB;
  } catch {
    return undefined;
  }
}

function openDatabase(factory: IDBFactory): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    let request: IDBOpenDBRequest;
    try {
      request = factory.open(DB_NAME, DB_VERSION);
    } catch (err) {
      reject(err as Error);
      return;
    }
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE, { keyPath: 'id' });
      if (!db.objectStoreNames.contains(REJECTED_STORE)) {
        db.createObjectStore(REJECTED_STORE, { keyPath: 'id' });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('offline-queue: open failed'));
    request.onblocked = () => reject(new Error('offline-queue: database blocked'));
  });
}

function idbPut(db: IDBDatabase, store: string, value: QueuedWrite): Promise<void> {
  return new Promise((resolve, reject) => {
    try {
      const tx = db.transaction(store, 'readwrite');
      tx.objectStore(store).put(value);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error ?? new Error('offline-queue: add failed'));
      tx.onabort = () => reject(tx.error ?? new Error('offline-queue: add aborted'));
    } catch (err) {
      reject(err as Error);
    }
  });
}

function idbGetAll<T extends QueuedWrite>(db: IDBDatabase, store: string): Promise<T[]> {
  return new Promise((resolve, reject) => {
    try {
      const tx = db.transaction(store, 'readonly');
      const req = tx.objectStore(store).getAll();
      req.onsuccess = () => resolve((req.result as T[] | undefined) ?? []);
      req.onerror = () => reject(req.error ?? new Error('offline-queue: getAll failed'));
    } catch (err) {
      reject(err as Error);
    }
  });
}

function idbDelete(db: IDBDatabase, store: string, id: string): Promise<void> {
  return new Promise((resolve, reject) => {
    try {
      const tx = db.transaction(store, 'readwrite');
      tx.objectStore(store).delete(id);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error ?? new Error('offline-queue: delete failed'));
      tx.onabort = () => reject(tx.error ?? new Error('offline-queue: delete aborted'));
    } catch (err) {
      reject(err as Error);
    }
  });
}

function generateId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `q_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

async function safeJson(res: Response): Promise<{ code?: string; message?: string } | null> {
  try {
    const text = await res.text();
    return text ? (JSON.parse(text) as { code?: string; message?: string }) : null;
  } catch {
    return null;
  }
}

/* ── the queue itself ──────────────────────────────────────────────────────────────────── */

type AttemptOutcome =
  | { kind: 'ok' }
  | { kind: 'retry' }
  | { kind: 'auth' }
  | { kind: 'drop'; code: string; message: string };

export function createOfflineQueue(options: OfflineQueueOptions = {}): OfflineQueueHandle {
  const idbFactory = options.indexedDB ?? safeGlobalIndexedDB();
  const fetchImpl: typeof fetch = options.fetchImpl ?? ((input, init) => fetch(input, init));

  let dbPromise: Promise<IDBDatabase> | null = null;
  let storageDisabled = false;

  let pending: QueuedWrite[] = [];
  let rejected: RejectedWrite[] = [];
  let authBlocked = false;
  let isFlushing = false;
  let isOnline = typeof navigator === 'undefined' ? true : navigator.onLine;

  let backoffMs = INITIAL_BACKOFF_MS;
  let backoffTimer: ReturnType<typeof setTimeout> | null = null;
  let flushPromise: Promise<FlushResult> | null = null;

  const listeners = new Set<() => void>();
  let snapshot: OfflineQueueSnapshot = {
    pendingCount: 0,
    pending: [],
    isFlushing,
    isOnline,
    authBlocked: false,
    rejected: [],
  };

  function refreshSnapshot() {
    snapshot = {
      pendingCount: pending.length,
      pending: [...pending],
      isFlushing,
      isOnline,
      authBlocked,
      rejected: [...rejected],
    };
    for (const listener of listeners) listener();
  }

  async function getDb(): Promise<IDBDatabase> {
    if (storageDisabled) throw new OfflineQueueUnavailableError();
    if (!idbFactory) throw new OfflineQueueUnavailableError('no indexedDB');
    dbPromise ??= openDatabase(idbFactory);
    return dbPromise;
  }

  async function hydrate() {
    try {
      const db = await getDb();
      const stored = await idbGetAll(db, STORE);
      // Merge rather than overwrite: an `enqueue()` racing this initial read must not be
      // clobbered, whichever of the two settles last.
      const merged = new Map(stored.map((w) => [w.id, w] as const));
      for (const w of pending) merged.set(w.id, w);
      pending = [...merged.values()].sort((a, b) => a.createdAt - b.createdAt);

      // Rejected writes come back too: the alert carrying somebody's ten minutes of typing is
      // no use if a reload — or the full-page navigation a Google sign-in is — erases it.
      const storedRejected = await idbGetAll<RejectedWrite>(db, REJECTED_STORE);
      const mergedRejected = new Map(storedRejected.map((w) => [w.id, w] as const));
      for (const w of rejected) mergedRejected.set(w.id, w);
      rejected = [...mergedRejected.values()].sort((a, b) => a.rejectedAt - b.rejectedAt);
      refreshSnapshot();
      // "Replay on app start": items left over from a *previous* session should not wait for
      // an 'online' event that, if the device is already connected, will never fire. Only
      // those — a write enqueued while this read was in flight is the caller's to flush, and
      // sending it from here races whatever that caller does next with it.
      if (stored.length > 0 && isOnline) void flush();
    } catch {
      storageDisabled = true;
      refreshSnapshot();
    }
  }

  async function enqueue(
    input: QueueRequestInput,
    opts: { replaceId?: string } = {},
  ): Promise<QueuedWrite> {
    const replaced = opts.replaceId ? pending.find((w) => w.id === opts.replaceId) : undefined;
    /* A corrected version of a queued write keeps that write's place in the queue and its id,
       so the outbox holds one report for the day rather than two the server would then have
       to arbitrate between. */
    const item: QueuedWrite = {
      id: replaced?.id ?? generateId(),
      createdAt: replaced?.createdAt ?? Date.now(),
      ...input,
    };
    try {
      const db = await getDb();
      await idbPut(db, STORE, item);
    } catch (err) {
      storageDisabled = true;
      throw err instanceof OfflineQueueUnavailableError
        ? err
        : new OfflineQueueUnavailableError(err instanceof Error ? err.message : undefined);
    }
    pending = replaced ? pending.map((w) => (w.id === item.id ? item : w)) : [...pending, item];
    refreshSnapshot();
    return item;
  }

  async function dequeue(id: string): Promise<void> {
    pending = pending.filter((w) => w.id !== id);
    refreshSnapshot();
    await safeRemove(id);
  }

  async function safeRemove(id: string) {
    try {
      const db = await getDb();
      await idbDelete(db, STORE, id);
    } catch {
      // Best-effort: the in-memory queue (the source of truth for this session) has already
      // dropped it. A stale row surviving in storage is picked up again, harmlessly, by the
      // next hydrate()'s id-merge.
    }
  }

  async function attempt(item: QueuedWrite): Promise<AttemptOutcome> {
    let res: Response;
    try {
      const headers: Record<string, string> = { Accept: 'application/json' };
      let body: string | undefined;
      if (item.body !== undefined) {
        headers['Content-Type'] = 'application/json';
        body = JSON.stringify(item.body);
      }
      res = await fetchImpl(`${API_BASE}${item.path}`, {
        method: item.method,
        credentials: 'same-origin',
        headers,
        body,
      });
    } catch {
      return { kind: 'retry' };
    }

    if (res.ok || res.status === 204) return { kind: 'ok' };

    const payload = await safeJson(res);
    const code = payload?.code ?? '';

    /*
     * Not a refusal of the write — a refusal of the caller. A report queued at 16:30 on a shell
     * with no signal sends when the phone finds wifi hours later, by which time a 12-hour
     * session has very often expired. Dropping it here deleted the report because of when the
     * signal came back. It stays queued; the caller signs in and flushes again.
     */
    if (AUTH_STATUSES.has(res.status)) return { kind: 'auth' };

    if (res.status >= 500) return { kind: 'retry' };
    if (res.status >= 400) {
      return {
        kind: 'drop',
        code: code || String(res.status),
        message: payload?.message ?? res.statusText,
      };
    }
    // An unexpected non-2xx/4xx/5xx (redirect swallowed by the fetch polyfill, etc.) — treat
    // as transient rather than guess it is the caller's fault.
    return { kind: 'retry' };
  }

  function scheduleBackoff() {
    if (backoffTimer) return;
    backoffTimer = setTimeout(() => {
      backoffTimer = null;
      if (isOnline) void flush();
    }, backoffMs);
    backoffMs = Math.min(backoffMs * 2, MAX_BACKOFF_MS);
  }

  function clearBackoff() {
    if (backoffTimer) {
      clearTimeout(backoffTimer);
      backoffTimer = null;
    }
    backoffMs = INITIAL_BACKOFF_MS;
  }

  async function runFlush(): Promise<FlushResult> {
    isFlushing = true;
    refreshSnapshot();
    let sent = 0;
    let stoppedForRetry = false;
    let stoppedForAuth = false;
    try {
      // Oldest-first, one at a time: order is the whole point (spec — a queued day 2 report
      // must not land before day 1's), and stopping at the first retryable failure preserves
      // it instead of racing every remaining item at once.
      while (pending.length > 0) {
        const item = pending[0]!;
        const outcome = await attempt(item);

        if (outcome.kind === 'ok') {
          authBlocked = false;
          await safeRemove(item.id);
          pending.shift();
          sent += 1;
          refreshSnapshot();
          continue;
        }

        if (outcome.kind === 'auth') {
          /* Nothing is removed. The write is intact in storage and in `pending`; what is
             missing is a session, and no number of retries supplies one. */
          authBlocked = true;
          stoppedForAuth = true;
          break;
        }

        if (outcome.kind === 'drop') {
          const rejectedWrite: RejectedWrite = {
            ...item,
            rejectedAt: Date.now(),
            errorCode: outcome.code,
            errorMessage: outcome.message,
          };
          /* Durable first, then the outbox row. The other order has a window in which the only
             copy of somebody's report is a variable in a tab that may not survive the minute. */
          await safeKeepRejected(rejectedWrite);
          await safeRemove(item.id);
          pending.shift();
          rejected.push(rejectedWrite);
          refreshSnapshot();
          continue; // a bad item must not block everything queued behind it
        }

        stoppedForRetry = true;
        break;
      }
    } finally {
      isFlushing = false;
      refreshSnapshot();
    }

    /* No backoff for an auth stop: retrying on a timer cannot produce a session. The caller
       flushes again when `/auth/me` succeeds. */
    if (stoppedForRetry && !stoppedForAuth) scheduleBackoff();
    else clearBackoff();

    return { sent, rejected: rejected.length, remaining: pending.length };
  }

  function flush(): Promise<FlushResult> {
    flushPromise ??= runFlush().finally(() => {
      flushPromise = null;
    });
    return flushPromise;
  }

  async function safeKeepRejected(item: RejectedWrite) {
    try {
      const db = await getDb();
      await idbPut(db, REJECTED_STORE, item);
    } catch {
      // Best-effort, same as `safeRemove`: the in-memory copy still reaches the banner this
      // session. Storage being unavailable is already surfaced by `enqueue` failing.
    }
  }

  function dismissRejected(id: string) {
    rejected = rejected.filter((r) => r.id !== id);
    refreshSnapshot();
    void (async () => {
      try {
        const db = await getDb();
        await idbDelete(db, REJECTED_STORE, id);
      } catch {
        /* see safeRemove */
      }
    })();
  }

  function subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
  }

  function getSnapshot(): OfflineQueueSnapshot {
    return snapshot;
  }

  function handleOnline() {
    isOnline = true;
    refreshSnapshot();
    void flush();
  }
  function handleOffline() {
    isOnline = false;
    refreshSnapshot();
  }
  if (typeof window !== 'undefined') {
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
  }

  function dispose() {
    if (typeof window !== 'undefined') {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    }
    clearBackoff();
  }

  void hydrate();

  return {
    enqueue,
    dequeue,
    flush,
    getPending: () => [...pending],
    getRejected: () => [...rejected],
    dismissRejected,
    subscribe,
    getSnapshot,
    dispose,
  };
}

/** One outbox for the whole app — every screen that queues a write shares it, so the status
 *  strip and the pending count agree no matter which screen is on top. */
export const offlineQueue = createOfflineQueue();

export function useOfflineQueueSnapshot(
  queue: OfflineQueueHandle = offlineQueue,
): OfflineQueueSnapshot {
  return useSyncExternalStore(queue.subscribe, queue.getSnapshot, queue.getSnapshot);
}

export function usePendingCount(queue: OfflineQueueHandle = offlineQueue): number {
  return useOfflineQueueSnapshot(queue).pendingCount;
}

/** The path a site report for `jobId` is POSTed to — shared so the queue and the page that
 *  reads it back out of the queue cannot drift apart. */
export function siteReportPath(jobId: string): string {
  return `/v1/jobs/${jobId}/site-reports`;
}
